<script setup>
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store.js';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  to: { type: [Object, String], default: '' },
  label: { type: String, default: '' },
  icon: { type: [String, Object], default: '' },
  expandable: { type: Boolean, default: false },
  isExpanded: { type: Boolean, default: false },
  isActive: { type: Boolean, default: false },
  hasActiveChild: { type: Boolean, default: false },
  getterKeys: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['toggle']);

const showBadge = useMapGetter(props.getterKeys.badge);
const dynamicCount = useMapGetter(props.getterKeys.count);
const count = computed(() =>
  dynamicCount.value > 99 ? '99+' : dynamicCount.value
);
</script>

<template>
  <component
    :is="to ? 'router-link' : 'div'"
    class="relative flex h-9 min-w-0 items-center gap-2 rounded-md px-1.5 transition-colors duration-100"
    role="button"
    draggable="false"
    :to="to"
    :title="label"
    :class="{
      'bg-n-alpha-2 font-medium text-n-slate-12': isActive && !hasActiveChild,
      'bg-n-alpha-1 font-medium text-n-slate-12': hasActiveChild,
      'text-n-slate-11 hover:bg-n-alpha-2': !isActive && !hasActiveChild,
    }"
    @click.stop="emit('toggle')"
  >
    <span
      v-if="isActive || hasActiveChild"
      class="absolute inset-y-2 start-0 w-0.5 rounded-full bg-n-brand"
    />
    <div
      v-if="icon"
      class="relative grid size-7 flex-shrink-0 place-content-center rounded-md"
      :class="
        isActive || hasActiveChild
          ? 'bg-n-brand/10 text-n-brand'
          : 'text-n-slate-10'
      "
    >
      <Icon :icon="icon" class="size-4" />
      <span
        v-if="showBadge"
        class="size-2 -top-px ltr:-right-px rtl:-left-px bg-n-brand absolute rounded-full border border-n-solid-2"
      />
    </div>
    <div
      class="flex items-center gap-1.5 flex-grow justify-between min-w-0 flex-1"
    >
      <span
        class="truncate"
        :class="{
          'text-body-main': !isActive && !hasActiveChild,
          'font-medium text-sm': isActive || hasActiveChild,
        }"
      >
        {{ label }}
      </span>
      <span
        v-if="dynamicCount && !expandable"
        class="inline-grid h-5 min-w-5 place-items-center rounded-full bg-n-slate-4 px-1 text-xxs font-medium leading-3 text-n-slate-12 dark:bg-n-slate-5 flex-shrink-0"
      >
        {{ count }}
      </span>
    </div>
    <span
      v-if="expandable"
      class="i-lucide-chevron-down size-3 flex-shrink-0 text-n-slate-10 transition-transform"
      :class="{ 'rotate-180': isExpanded }"
      @click.stop="emit('toggle')"
    />
  </component>
</template>
